import { useCallback, useState } from "react";
import { CompactActionButton } from "@louise/moon-ui";

import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  deviceList,
  recordedDeviceLabel,
} from "@src/packages/injection-coach/deviceChoices";
import { useMedicationFormCorrection } from "@src/packages/care/useMedicationFormCorrection";
import { deviceDecision } from "@src/packages/injection-coach/deviceDecision";
import {
  Card,
  DeviceConfirmation,
  DeviceQuestion,
  InjectionCoachWalk,
  RouteQuestion,
} from "@src/packages/injection-coach/components/InjectionCoachWalk";

import {
  findCoachableDose,
  resolveInjectionCoach,
  protocolsForEligibility,
  recordedFormFor,
  unansweredAxis,
  type EligibilityInput,
} from "./injectionCoachEligibility";

const ns = "care.injection_coach";

/**
 * The injection coach on the trigger-shot step.
 *
 * What is left here after the walk was extracted is everything that is about
 * *this dose* rather than about the procedure: which scheduled dose she is on,
 * which device her prescription records, and recording the dose once the walk
 * completes. The steps themselves live in `InjectionCoachWalk`, which any
 * surface can render.
 *
 * That split is the point. This screen is the only place a completed walk can be
 * recorded against the right dose, so it is the only place that records one — a
 * factsheet offering the same steps records nothing, and #2267 forbids
 * manufacturing a dose event from anything less than an approved signal.
 */

type Props = EligibilityInput & {
  /**
   * Her answer to the device question, when her prescription does not record one.
   *
   * Held for the session, *and* written to her calendar — see
   * `useMedicationFormCorrection` below. The comment here used to say there was
   * no field to persist it to; there is, and has been since the coach started
   * reading `medication.form` rather than asking. The dose this panel is
   * rendered against carries its own id, so the record to write to was never in
   * doubt.
   */
  onConfirmForm: (method: string) => void;
  /**
   * Her answer to the route question, when her prescription does not record one.
   *
   * Held the same way and for the same reason as the device, with one difference
   * that matters: the two routes of urinary hCG are two procedures, not two
   * devices for one. A wrong answer here would guide her to the wrong site, so
   * nothing infers it — her prescription says, or she does.
   */
  onConfirmRoute: (route: "intramuscular" | "subcutaneous") => void;
  onCallClinic: () => void;
  /**
   * Logs the dose once the walk completes.
   *
   * Awaited, and a rejection is shown: the completion screen tells her the trigger
   * shot is recorded, so it may only say so once something has recorded it.
   */
  onCompleted: (medicationId: string) => Promise<void>;
};

/** Where the dose this walk delivered has got to in her record. */
type RecordingState = "idle" | "saving" | "recorded" | "failed";

export function InjectionCoachPanel({
  onConfirmForm,
  onConfirmRoute,
  onCallClinic,
  onCompleted,
  ...eligibility
}: Props) {
  const t = useTranslations();
  const [recording, setRecording] = useState<RecordingState>("idle");
  /**
   * The same three answers the coach sheet holds about her device.
   *
   * This panel used to ask its own, shorter question: a list of buttons, no
   * confirmation of what her calendar already records, and no way to say she
   * has none of them. Same patient, same medication, two behaviours decided by
   * where she tapped. The decision is shared now — see `deviceDecision` — and
   * these are the three things she can press.
   */
  const [formConfirmed, setFormConfirmed] = useState(false);
  const [formDeclined, setFormDeclined] = useState(false);
  const [noDeviceMatches, setNoDeviceMatches] = useState(false);

  const candidate = resolveInjectionCoach(eligibility);
  // The dose behind this panel, whether or not a procedure has been settled on.
  // `candidate` is null for exactly as long as a question is open, which is the
  // moment her answer is worth storing.
  const doseMedicationId = findCoachableDose(eligibility)?.medicationId ?? null;
  const medicationId = candidate?.medicationId ?? null;
  // Her answer reaches the calendar from here too, so the same question is not
  // asked again tomorrow on a surface that already heard it.
  const correctMedicationForm = useMedicationFormCorrection(
    doseMedicationId,
    recordedFormFor(eligibility),
  );
  const answerDevice = useCallback(
    (method: string) => {
      onConfirmForm(method);
      correctMedicationForm(method);
    },
    [correctMedicationForm, onConfirmForm],
  );

  const recordDose = useCallback(async () => {
    if (!medicationId) {
      // The walk completed against a dose we can no longer name. Nothing to
      // record, and nothing that lets us claim we did.
      setRecording("failed");
      return;
    }
    setRecording("saving");
    try {
      await onCompleted(medicationId);
      setRecording("recorded");
    } catch {
      setRecording("failed");
    }
  }, [medicationId, onCompleted]);

  if (!candidate) {
    // There is an injectable dose here, and one thing about the procedure is
    // still unnamed — her prescription does not record it and she has not told
    // us. Which thing is asked of the registry rather than assumed: Ovidrel's
    // two protocols differ by device, urinary hCG's differ by route, and a
    // medication whose protocols agree on both asks nothing.
    //
    // Asked only when it is genuinely open: when the prescription says, she is
    // not made to answer a question the app can already answer.
    const asking = unansweredAxis(eligibility);
    if (asking === "device") {
      const device = deviceDecision({
        protocols: protocolsForEligibility(eligibility),
        recordedForm: recordedFormFor(eligibility),
        answered: eligibility.confirmedForm,
        confirmed: formConfirmed,
        declined: formDeclined,
        noneOfThese: noDeviceMatches,
      });

      if (device.stage === "unguided") {
        return (
          <Card>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 15, lineHeight: 22, color: EMBER.colors.text }}
            >
              {t(`${ns}.we_only_have_steps_for_these_presentations`, {
                params: { devices: deviceList(device.methods, t) },
              })}
            </Text>
            {/* A way back. This card replaced the question with no button at
                all, so one tap ended the coach for as long as the step stayed
                on screen — and the step does not unmount on its own. */}
            <CompactActionButton
              testID="injection-coach-device-reconsider"
              variant="secondary"
              label={t(`${ns}.which_device_do_you_have`)}
              onPress={() => {
                setNoDeviceMatches(false);
                setFormDeclined(false);
                setFormConfirmed(false);
              }}
            />
          </Card>
        );
      }

      if (device.stage === "confirm" && device.proposed) {
        return (
          <DeviceConfirmation
            t={t}
            label={recordedDeviceLabel(device.proposed, t)}
            fromHerRecord={recordedFormFor(eligibility) !== null}
            onlyPresentation={device.methods.length <= 1}
            onConfirm={() => {
              setFormConfirmed(true);
              // Only when it is an answer. Confirming a device this medication
              // is not dispensed as leaves `decided` null and takes her to the
              // list instead — see `deviceDecision`.
              if (device.proposalIsGuided && device.proposed) {
                answerDevice(device.proposed);
              }
            }}
            onChange={() => setFormDeclined(true)}
          />
        );
      }

      return (
        <DeviceQuestion
          t={t}
          methods={device.methods}
          onConfirmForm={answerDevice}
          onNoneOfThese={() => setNoDeviceMatches(true)}
        />
      );
    }
    if (asking === "route") {
      return <RouteQuestion t={t} onConfirmRoute={onConfirmRoute} />;
    }
    return null;
  }

  return (
    <InjectionCoachWalk
      protocol={candidate.protocol}
      brandName={candidate.brandName}
      patientMedicationId={candidate.medicationId}
      onCallClinic={onCallClinic}
      // Deliberately not recording here. Reaching the last step is not evidence
      // that an injection happened — she may have walked the steps to read them
      // — and #2267 forbids manufacturing a success from an ambiguous signal.
      // The completion screen asks, and her answer is the approved signal.
      // Whichever way she leaves, the next walk starts clean. A stale
      // "recorded" carried into a fresh walk would tell her a dose was saved
      // before anything had saved one.
      onFinished={() => setRecording("idle")}
      // The injection happened either way; only the record is in question. So the
      // headline follows the save, and "recorded" waits until it is true.
      completionHeadline={(status) =>
        status === "aborted"
          ? `${ns}.stopped_for_now`
          : recording === "idle"
            ? `${ns}.did_you_inject_it`
            : recording === "failed"
              ? `${ns}.dose_not_recorded`
              : recording === "recorded"
                ? `${ns}.dose_recorded`
                : `${ns}.recording_your_dose`
      }
      completionExtra={(status) => {
        if (status !== "complete") return null;
        // She answers. Nothing else does.
        if (recording === "idle") {
          return (
            <CompactActionButton
              testID="injection-coach-confirm-dose"
              variant="primary"
              label={t(`${ns}.yes_i_injected_it`)}
              onPress={() => void recordDose()}
            />
          );
        }
        if (recording === "failed") {
          return (
            <CompactActionButton
              testID="injection-coach-record-dose-again"
              variant="primary"
              label={t(`${ns}.save_dose_again`)}
              onPress={() => void recordDose()}
            />
          );
        }
        return null;
      }}
    />
  );
}
